import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import AppTableBody from "components/AppTable/AppTableBody";
import AppTableFooters from "components/AppTable/AppTableFooters";
import AppTableHeaders from "components/AppTable/AppTableHeaders";
import TableField from "components/AppTable/TableField";
import ViewRecordDrawer from "components/Drawers/Records/ViewRecordDrawer";
import FieldFormatter from "components/Records/Formatters/FieldFormatter";
import useModule from "hooks/useModule";
import pluralize from "pluralize";
import { useMemo, useState } from "react";
import { Alert } from "reactstrap";
import _ from "underscore";

const noData = [];

/**
 * SummaryCardTableView - A  readonly table view for the summary card widget (MultiReference Only)
 * @param {Array<records>} The records to display in the table
 * @param {Object} fieldSchema - The field schema for the parent record
 */
const SummaryCardTableView = ({
  records,
  fieldSchema,
  maxHeight = 400,
  moduleAPIName = "", // Used for empty state
}) => {
  const { getModule } = useModule();
  const [module, setModule] = useState(null);
  const isDynamic = !fieldSchema?.SourcesList?.startsWith("Tenant");
  const summaryFields = fieldSchema?.RecordSummaryFields;
  const [viewRecord, setViewRecord] = useState(null);

  const data = useMemo(() => {
    if (!records?.length > 0 || !summaryFields || !getModule) return null;

    const mods = {};
    const columnsMap = {};
    const dataMap = {};

    for (const record of records) {
      const recordModuleName = record.ModuleAPIName || record._ModuleAPIName;
      const recordSummaryFields = [...summaryFields[recordModuleName].Fields];
      let recordModule;

      // If the module is not already loaded, load it.
      if (!mods[recordModuleName]) {
        recordModule = getModule(recordModuleName);
        mods[recordModuleName] = recordModule;
      } else {
        recordModule = mods[recordModuleName];
      }

      // Setup columns map for module if not already setup.
      if (!columnsMap[recordModuleName]) {
        columnsMap[recordModuleName] = recordSummaryFields.map(
          (fieldAPIName) => {
            const field = _.findWhere(recordModule.Fields, {
              APIName: fieldAPIName,
            });

            return {
              accesorKey: fieldAPIName,
              id: fieldAPIName,
              cell: (item) => {
                const row = item.row.original;

                return (
                  <FieldFormatter
                    field={field}
                    value={item.getValue()}
                    record={row}
                    recordID={row.id}
                    moduleAPIName={row._ModuleAPIName || row.ModuleAPIName}
                    table={true}
                    forceReadOnly={true}
                  />
                );
              },
              header: () => field?.Name,
            };
          }
        );

        columnsMap[recordModuleName].unshift({
          id: "_RecordName",
          cell: (item) => (
            <div className="px-3">
              <TableField fieldMap={{}} item={item} />
            </div>
          ),
          header: () => <div className="px-3">Record Name</div>,
        });
      }

      // Setup data map for module if not already setup.
      if (!dataMap[recordModuleName]) {
        dataMap[recordModuleName] = [];
      }

      // Add record to data map.
      dataMap[recordModuleName].push(record);
    }

    return { mods, columnsMap, dataMap };
  }, [records, getModule, summaryFields]);

  if (records && data && !isDynamic && !module) {
    setModule(
      data.mods[records[0].ModuleAPIName] ||
        data.mods[records[0]._ModuleAPIName]
    );
  }

  const table = useReactTable({
    data: data?.dataMap?.[module?.APIName] ?? noData,
    columns: data?.columnsMap?.[module?.APIName] ?? noData,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!data) {
    let name = "No records selected. Click edit to choose records.";

    if (moduleAPIName) {
      let moduleName = getModule(moduleAPIName)?.Name;

      if (moduleName) {
        moduleName = pluralize(moduleName);
        name = `No ${moduleName} selected. Click Edit to choose ${moduleName}.`;
      }
    }

    return (
      <Alert color="superlight" className="font-weight-500 m-2">
        {name}
      </Alert>
    );
  }

  if (!data || !module) return null;

  return (
    <div
      className="table-responsive"
      style={{ maxHeight: maxHeight, overflowY: "auto" }}
    >
      <>
        <table style={{ minWidth: "100%" }}>
          <AppTableHeaders table={table} />
          <AppTableBody table={table} />
          <AppTableFooters table={table} />
        </table>
      </>
      {viewRecord && (
        <ViewRecordDrawer
          open={true}
          onClose={() => setViewRecord(null)}
          recordID={viewRecord?.recordID}
          module={viewRecord?.module}
        />
      )}
    </div>
  );
};

export default SummaryCardTableView;
